import { cloneElement, useEffect, useState } from 'react';
import { useLocation, useOutlet } from 'react-router-dom';

const LEAVE_MS = 220;
const ENTER_MS = 280;

type Stage = 'idle' | 'leaving' | 'entering';

export function AuthRouteTransition() {
  const outlet = useOutlet();
  const { pathname } = useLocation();
  const [displayedPath, setDisplayedPath] = useState(pathname);
  const [stage, setStage] = useState<Stage>('idle');

  // Derive-during-render: kick off the leave when pathname changes.
  // (Avoids setState-in-effect; React allows setState in render for
  // derived state.)
  if (pathname !== displayedPath && stage === 'idle') {
    setStage('leaving');
  }

  useEffect(() => {
    if (stage === 'leaving') {
      const t = window.setTimeout(() => {
        setDisplayedPath(pathname);
        setStage('entering');
      }, LEAVE_MS);
      return () => window.clearTimeout(t);
    }
    if (stage === 'entering') {
      const t = window.setTimeout(() => setStage('idle'), ENTER_MS);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [stage, pathname]);

  const child = outlet ? cloneElement(outlet, { key: displayedPath }) : null;

  const stageStyle =
    stage === 'leaving'
      ? {
          opacity: 0,
          transform: 'translateX(-20px)',
          transition: `opacity ${LEAVE_MS}ms ease, transform ${LEAVE_MS}ms ease`,
        }
      : stage === 'entering'
        ? { animation: `auth-pane-in ${ENTER_MS}ms ease forwards` }
        : undefined;

  const stageClass =
    stage === 'leaving' ? 'auth-pane-leaving' : stage === 'entering' ? 'auth-pane-entering' : '';

  return (
    <div className={`w-full ${stageClass}`} style={stageStyle}>
      {child}
    </div>
  );
}
